/* eslint-disable react/jsx-no-target-blank */
import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { Dropdown, TextField } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { GoalCreationWizardContext } from '../goal-creation-wizard-context';
import { getStepStyles } from '../../../conversion-wizard-components/src/shared-styles/shared-content-style';
import { getExpressionOperatorOptions } from './goal-creation-options';
import { validateString, validateProtocol } from '../shared/validation-util';

const DestinationUrlSettingUnstyled = (props) => {
  const {
    classes,
    updateGoalProps,
    showError,
    resetShowError,
  } = props;
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const [selectedOperator, setSelectedOperator] = useState(wizardContext.goalContext.Operator);
  const [inputUrl, setInputUrl] = useState(wizardContext.goalContext.UrlString);
  const expressionOperatorOptions = useMemo(() => getExpressionOperatorOptions(i18n), [i18n]);

  const [urlErrorMessage, setUrlErrorMessage] = useState('');
  const [operatorErrorMessage, setOperatorErrorMesage] = useState('');

  const getOperatorErrorMessage = useCallback((op) => {
    if (!op) {
      return i18n.getString(_TL_('Select a conversion tracking scenario.'));
    }
    return '';
  }, [i18n]);

  const getUrlErrorMesssage = useCallback((op, url) => {
    if (url.trim().length === 0) {
      return i18n.getString(_TL_('Enter a destination.'));
    } else if (!validateString(url)) {
      return i18n.getString(_TL_('Enter a valid URL string.'));
    } else if (url.trim().length > 10000) {
      return i18n.getString(_TL_('URL length should not exceed 10000.'));
    } else if ((op === 'EqualsTo' || op === 'BeginsWith') && !validateProtocol(url)) {
      return i18n.getString(_TL_('URL should start with protocol. For example: https://'));
    }
    return '';
  }, [i18n]);

  useEffect(() => {
    if (showError) {
      setOperatorErrorMesage(getOperatorErrorMessage(selectedOperator));
      setUrlErrorMessage(getUrlErrorMesssage(selectedOperator, inputUrl));
      resetShowError();
    }
  }, [getOperatorErrorMessage, getUrlErrorMesssage, inputUrl, resetShowError, selectedOperator, showError]);

  const onChangeOperator = useCallback((event, value) => {
    setSelectedOperator(value.key);
    setOperatorErrorMesage('');
    setUrlErrorMessage(getUrlErrorMesssage(value.key, inputUrl));
    updateGoalProps({ Operator: value.key, UrlString: inputUrl });
  }, [getUrlErrorMesssage, inputUrl, updateGoalProps]);

  const onChangeUrl = useCallback((event, value) => {
    setInputUrl(value);
    const errorMessage = getUrlErrorMesssage(selectedOperator, value);
    setUrlErrorMessage(errorMessage);
    updateGoalProps({ UrlString: _.isEmpty(errorMessage) ? value.trim() : undefined });
  }, [getUrlErrorMesssage, selectedOperator, updateGoalProps]);

  const title = i18n.getString(_TL_('Destination URL'));

  const operatorMessage = useMemo(() => {
    const map = {};
    _.each(expressionOperatorOptions, (item) => {
      map[item.key] = { description: item.description, helpLink: item.link };
    });

    return map;
  }, [expressionOperatorOptions]);

  return (
    <div className="destination-url-setting">
      <div className={classes.nonAccordionTitle}>
        <span className="title">{title}</span>
        <HelpButton
          aria-label={title}
          topic="pop_BA_UET_DestinationURL"
        />
      </div>
      <div className={classes.rowContainer}>
        <div className="destination-operator">
          <Dropdown
            selectedKey={selectedOperator}
            options={expressionOperatorOptions}
            onChange={onChangeOperator}
            styles={{ dropdown: { width: 180 } }}
          />
          {!_.isEmpty(operatorErrorMessage) &&
          <div className="row" role="alert">
            <p className={classes.errorText}>{operatorErrorMessage}</p>
          </div>}
        </div>
        <div className="url-field">
          <TextField
            value={inputUrl}
            onChange={onChangeUrl}
            styles={{ fieldGroup: { width: 180 } }}
          />
          {!_.isEmpty(urlErrorMessage) && (
            <div className="row" role="alert">
              <p className={classes.errorText}>{urlErrorMessage}</p>
            </div>
          )}
        </div>
      </div>
      {/* operator explanation */}
      {!_.isEmpty(selectedOperator) && (
        <div className={classes.inactiveText} id="operatorDescription">
          <span>
            {operatorMessage[selectedOperator].description} <a target="_blank" className="btn-link" href={operatorMessage[selectedOperator].helpLink}>{i18n.getString(_TL_('Learn more'))}</a>
          </span>
        </div>
      )}
    </div>
  );
};

DestinationUrlSettingUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  updateGoalProps: PropTypes.func.isRequired,
  showError: PropTypes.bool.isRequired,
  resetShowError: PropTypes.func.isRequired,
};

export const DestinationUrlSetting = withDefaultStyles(DestinationUrlSettingUnstyled, getStepStyles);
